import React, { useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useParams, useLocation } from "react-router-dom";
import { ApolloProvider, useQuery } from "@apollo/client";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import ViewportHelmet from "../components/ViewportHelmet";
import WebViewHeader, { addEntryIdAnchors } from "../components/WebViewHeader";
import PageContextProvider from "../PageContext";
import resolveVariables from "../craft/dataResolvers/resolveVariables";
import { resolveGlobals, resolveEntry } from "../craft/dataResolvers";
import { formatDate, debugLog } from "../utils";
import { getPreviewParams } from "../utils/previewParams";
import Header from "../components/Header";
import Paginator from "../components/Paginator";
import createHamburgerMenu from "../utils/hamburgerMenu";
import injectHamburgerMenuStyles from "../utils/hamburgerMenuStyles";
import { ensureHeadingIds } from "../utils/createToc";
import Topic from "../components/Topic";
import Footer from "../components/Footer";
import PreviewErrorBoundary from "../components/PreviewErrorBoundary";
import { createTopicClient } from "../apollo/topicPreviewClient";
import LIVE_TOPIC_PREVIEW_QUERY from "../craft/dynamicQueries/LiveTopicPreviewQuery";
import { setPreview } from "../utils/previewFlag";

const TopicPreviewTemplate = ({
  topicId,
  siteId,
  topicPreviewClient,
  isProductionView = false,
  isWebView = true,
}) => {
  // --- set the flag BEFORE any resolvers are invoked ------------------
  setPreview(!isProductionView); // true when we’re in preview mode
  /* — detect if we will use paged.js ——— */
  const shouldUsePaginator = useMemo(() => {
    if (typeof window === "undefined") return true; // SSR fallback
    const ua = navigator.userAgent;
    const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua);
    const isChrome = /Chrome/.test(ua);
    if (isWebView) return false; // forced web view
    return !(isSafari && !isChrome); // skip on Safari
  }, [isWebView]);

  const { data, loading, error } = useQuery(LIVE_TOPIC_PREVIEW_QUERY, {
    variables: { id: topicId, siteId },
    client: topicPreviewClient, // Ensure the correct client is used
  });

  const containerRef = useRef(null);
  const tocCreatedRef = useRef(false);

  /* -----------------------------------------
   * Build TOC + hamburger after DOM render
   * ----------------------------------------- */
  useEffect(() => {
    if (
      !shouldUsePaginator && // only in web mode
      containerRef.current && // DOM ready
      !tocCreatedRef.current
    ) {
      try {
        injectHamburgerMenuStyles(); // ① add base styles

        /* ② ensure every heading has an ID (HeadingWrapper already does,
              but this covers edge cases)                            */
        ensureHeadingIds(containerRef.current, [
          ".title-chapter",
          ".title-topic",
        ]);
        /* collect headings the same way createTocWithHamburgerMenu expects */
        const headings = [
          ...containerRef.current.querySelectorAll(
            ".title-chapter, .title-topic"
          ),
        ].map(el => ({
          element: el,
          id: el.id,
          level: el.classList.contains("title-chapter") ? 1 : 2,
          text: el.textContent.trim(),
        }));

        if (headings.length) {
          createHamburgerMenu({
            headings,
            container: containerRef.current,
            context: "preview",
          });
          tocCreatedRef.current = true;
        }
      } catch (err) {
        console.error("Hamburger menu creation failed:", err);
      }
    }
  }, [shouldUsePaginator, data]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={64} />
      </Box>
    );
  }

  if (error) {
    console.error("Error fetching topic data:", error);
    console.error(
      "GraphQL Endpoint:",
      process.env.GATSBY_CRAFT_API_IMPORTEXPORT_URL
    );
    return (
      <PreviewErrorBoundary
        initialError={{ error }}
        initialMessage="Error loading topic preview."
      >
        {/* no children – boundary shows the fallback UI */}
      </PreviewErrorBoundary>
    );
  }

  debugLog("data", data);
  // Data is available
  const { uiText, contact } = resolveGlobals(data.globalSets);

  // ────────────────────────────────────────────────────────────────
  // 1) Build the id → string map that applyVariables() needs
  //    (no scoped vars and no manual context on this route)
  const { globalVariables } = data;
  const { resolvedByGlobalId: variableValues } = resolveVariables({
    globalVariables,
    scopedVariables: [], // none in topic preview
    manualContext: {}, // no product/category/feature context
  });
  // ────────────────────────────────────────────────────────────────

  const glossary = data.glossary.map((g, i) =>
    resolveEntry(g, i /* idx */, variableValues)
  );

  let resolvedTopic = {};

  // Define content builder fields to check
  const contentBuilderFields = [
    "topicArticleContentBuilder",
    "topicArticleContentBuilderComposableEntries",
    "topicArticleStepBuilder",
    "stepArticlesComposableEntries",
    "step",
  ];

  // Check if data.entry has any of the content builder fields
  const hasContentBuilder = contentBuilderFields.some(
    field => data.entry && data.entry[field]
  );

  if (hasContentBuilder) {
    // Entry is a topic article or similar, resolve as usual
    resolvedTopic = resolveEntry(data.entry, 0, variableValues);
  } else if (data.entry) {
    // Entry is likely a block, wrap it in a dummy topic article
    const emptyTopic = {
      id: data.entry.id || topicId || 1,
      userTitle: data.entry.userTitle || "Block Preview",
      title: data.entry.userTitle || "Block Preview",
      typeHandle: "articleLongform", // Only one block so articleLongform will work
      topicArticleContentBuilder: [data.entry], // Include the block
    };
    resolvedTopic = resolveEntry(emptyTopic, 0, variableValues);
  } else {
    // Handle invalid or null entry
    return <div>Invalid entry for topic preview.</div>;
  }

  const initialState = {
    chrome: {
      title: resolvedTopic?.userTitle || "Placeholder Title",
      logo: resolvedTopic?.logo || "",
      color: resolvedTopic?.color || "#000000",
    },
    uiText,
    contact,
    glossary,
    formattedDate: formatDate(new Date()),
    productCategory: [], // no productCategory avail on topic query
    productFeatures: [], // no productFeaturesPerLanguage avail on topic query
    manualId: null, // no manual avail on topic query
    isPreview: !isProductionView,
    isWebView,
  };

  const pageLayoutType =
    initialState.chrome?.manual?.pageLayoutType || "letter"; // no manual passed (yet)
  let pageClass = "letter";
  switch (pageLayoutType) {
    case "QSG":
      pageClass = "QSG";
      break;
    // case "legal": pageClass = "legal"; break;
    default:
      pageClass = "letter";
      break;
  }
  const isQSG = pageLayoutType === "QSG";
  if (initialState.isPreview) {
    pageClass += " preview-mode ";
  }
  // Add non-paginated class if paginator is not being used
  if (!shouldUsePaginator) {
    pageClass += ` non-paginated-mode sm:w-full sm:max-w-full sm:mx-4 `; // viewport for mobile deviecvs
  }

  // Minimal manual-like object for WebViewHeader — topic preview has no full manual
  const topicManual = {
    id: null,
    siteId: siteId,
    userTitle: resolvedTopic?.userTitle || resolvedTopic?.title || 'Topic Preview',
    title: resolvedTopic?.userTitle || resolvedTopic?.title || 'Topic Preview',
    logo: null,
    productFamily: null,
    variant: null,
    chapters: [], // no chapters — scroll tracker won't update chapter breadcrumbs
  };

  return (
    <PageContextProvider initialState={initialState}>
      {/* Non-paged web view (Safari / ?web=1) */}
      {!shouldUsePaginator && (
        <>
          {isWebView && <ViewportHelmet />}
          <WebViewHeader
            manual={topicManual}
            containerRef={containerRef}
            defaultCollapsed
            isPreview={!isProductionView}
            isProductionView={isProductionView}
          />
          <div className="non-paginated-background">
            <div className="non-paginated-content-wrapper">
              <div ref={containerRef} className={`topic-preview ${pageClass}`}>
                <Header />
                <div className="chapter">
                  {/* add chapter padding */}
                  <Topic entry={resolvedTopic} isSolo={false} changedType={0} />
                </div>
                <Footer />
                <div id="preview" />
              </div>
            </div>
          </div>
        </>
      )}

      {/*  Paged / PDF view (Chrome print or ?web=0) */}
      {shouldUsePaginator && (
        <div ref={containerRef} className={pageClass}>
          <Paginator>
            <Header />
            <div className={isQSG ? "mx-page-margin-max" : "mx-page-margin"}>
              <div className={isQSG ? "max-w-content-max" : "max-w-content"}>
                <div
                  id="my-toc-content"
                  className="toc-container ml-page-margin max-w-contentmaxmargin"
                >
                  {/* TOC will be populated by createPaginatedToc() in Paginator */}
                </div>
                <div className="chapter">
                  <Topic entry={resolvedTopic} isSolo={false} changedType={0} />
                </div>
              </div>
            </div>
            <Footer />
            <div id="preview" />
          </Paginator>
        </div>
      )}
    </PageContextProvider>
  );
};

TopicPreviewTemplate.propTypes = {
  topicId: PropTypes.string.isRequired,
  siteId: PropTypes.string.isRequired,
  topicPreviewClient: PropTypes.object.isRequired, // Add PropTypes for client
  isProductionView: PropTypes.bool,
  isWebView: PropTypes.bool,
};

// Wrap TopicPreviewTemplate with ApolloProvider
const TopicPreviewTemplateWithApollo = () => {
  // Extract route parameters
  const { topicId, siteId } = useParams();
  const params = useParams();
  debugLog("params", params);
  // Extract query parameters from the URL
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);

  // Read preview params — URL takes priority, sessionStorage fills in the
  // gaps when the user navigates to a page that doesn't carry the params.
  const storedPreviewParams = getPreviewParams();

  const token = urlParams.get("token") ?? storedPreviewParams['token'] ?? null;
  const xCraftPreview = urlParams.get("x-craft-preview") ?? storedPreviewParams['x-craft-preview'] ?? null;
  const xCraftLivePreview = urlParams.get("x-craft-live-preview") ?? storedPreviewParams['x-craft-live-preview'] ?? null;
  const rawApiUrl = urlParams.get("api-url") ?? storedPreviewParams['api-url'] ?? null;
  const apiUrl =
    rawApiUrl && rawApiUrl.toLowerCase() !== "null"
      ? decodeURIComponent(rawApiUrl)
      : null;
  debugLog("Extracted apiUrl:", apiUrl, "Type:", typeof apiUrl);
  const isProductionViewDisabled =
    urlParams.get("live-production") === "0" ||
    urlParams.get("production") === "0" ||
    false;
  const isProductionView = !isProductionViewDisabled && !xCraftLivePreview; // production view not explictly disabled and we are not previewing from craft
  const isWebViewDisabled =
    urlParams.get("web") === "0" || urlParams.get("web-view") === "0" || false;
  const isWebView = !isWebViewDisabled;

  // const client = createTopicClient(token, xCraftPreview, xCraftLivePreview, apiUrl);
  // Memoize the Apollo Client instance to prevent unnecessary re-creations
  const client = useMemo(
    () => createTopicClient(token, xCraftPreview, xCraftLivePreview, apiUrl),
    [token, xCraftPreview, xCraftLivePreview, apiUrl]
  );

  // Security: Ensure that route parameters are present
  if (!topicId || !siteId) {
    return <p>Invalid preview URL. Missing topic or site parameters.</p>;
  }

  return (
    <ApolloProvider client={client}>
      <PreviewErrorBoundary>
        <TopicPreviewTemplate
          topicId={topicId}
          siteId={siteId}
          topicPreviewClient={client}
          isProductionView={isProductionView}
          isWebView={isWebView}
        />
      </PreviewErrorBoundary>
    </ApolloProvider>
  );
};

export default TopicPreviewTemplateWithApollo;
